import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTop from "@/components/feature/ScrollTop";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { siteInfo } from "@/mocks/channels";
import { brandValues, milestones, quotes, faqs } from "@/mocks/extra";
import { useSeo } from "@/hooks/useSeo";

export default function About() {
  useSeo({
    title: "关于我们 - 全网吃瓜 · 品牌介绍与内容理念",
    description:
      "了解全网吃瓜：一个中立呈现网友评论、热议观点与舆论风向的聚合门户。认识我们的定位、内容原则、用户口碑与联系方式。",
    keywords: "全网吃瓜,关于我们,内容原则,品牌理念,网友评论",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于全网吃瓜",
        description: siteInfo.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "全网吃瓜",
        slogan: siteInfo.slogan,
        email: siteInfo.mail,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* 首屏 */}
        <section className="relative overflow-hidden bg-background-100/80">
          <div className="wrap grid gap-10 py-14 md:py-20 lg:grid-cols-[1.3fr_1fr] lg:items-center">
            <div>
              <nav aria-label="面包屑" className="text-sm text-foreground-500">
                <ol className="flex flex-wrap items-center gap-1.5">
                  <li>
                    <Link to="/" className="hover:text-primary-600">
                      首页
                    </Link>
                  </li>
                  <li className="text-foreground-300">/</li>
                  <li className="font-medium text-foreground-800">关于我们</li>
                </ol>
              </nav>
              <h1 className="mt-5 font-display text-3xl font-bold leading-tight text-foreground-900 md:text-5xl">
                我们在做的事：
                <br />
                让「大家怎么想」被看见
              </h1>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteInfo.intro} 我们不制造热点，而是热点的「翻译官」与「整理师」。
              </p>
              <div className="mt-7 flex flex-wrap gap-2">
                <a
                  href="#faq"
                  className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-question-answer-line" aria-hidden="true" /> 查看常见问题
                </a>
                <a
                  href={`mailto:${siteInfo.mail}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-200 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  rel="nofollow"
                >
                  <i className="ri-mail-line" aria-hidden="true" /> 联系我们
                </a>
              </div>
            </div>

            {/* 一句话价值卡片 */}
            <div className="relative">
              <div className="absolute -right-3 -top-3 h-24 w-24 rounded-2xl bg-accent-200/70" aria-hidden="true" />
              <div className="absolute -bottom-4 -left-3 h-24 w-24 rounded-2xl bg-primary-200/60" aria-hidden="true" />
              <div className="relative rounded-3xl bg-foreground-900 p-7 text-background-50">
                <p className="font-label text-2xl leading-relaxed text-accent-200">
                  「有热度，更要有态度；看热闹，更要看门道。」
                </p>
                <div className="mt-6 flex flex-wrap gap-6 text-sm">
                  <div>
                    <p className="font-display text-2xl font-bold text-primary-400">多元</p>
                    <p className="text-background-300">并列多方声音</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-bold text-accent-400">客观</p>
                    <p className="text-background-300">不预设立场</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-bold text-secondary-300">克制</p>
                    <p className="text-background-300">不传不实信息</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 定位与理念 */}
        <section className="wrap pt-14 md:pt-20">
          <div className="mb-8 max-w-2xl">
            <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-compass-3-line" aria-hidden="true" /> 我们的定位
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">吃瓜群众的态度放大镜</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              我们相信，评论区的每一条声音都代表一种真实情绪。{siteInfo.brand}把散落在各处的讨论聚合、归类、结构化，
              让你不必亲自翻完所有楼，也能在几十秒内抓住一件事的主流看法与舆论走向。
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {brandValues.map((v) => (
              <div key={v.title} className="card-tint rounded-2xl p-6 transition-transform hover:-translate-y-1">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-700">
                  <i className={v.icon} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 内容原则 */}
        <section className="mt-16 bg-background-100 py-14 md:py-16">
          <div className="wrap grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
                <i className="ri-shield-check-line" aria-hidden="true" /> 内容原则
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">我们这样守护讨论的善意</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                围观可以热情，但不能失守底线。以下原则是我们编辑与呈现内容时的基本准则。
              </p>
            </div>
            <ol className="space-y-3">
              {[
                "只聚合「讨论本身」，不替网友下结论，明确标注为观点整理而非事实报道。",
                "凡涉事实争议，标注「信源待核」，不传播未经证实的说法与谣言。",
                "并列呈现支持、中立、反对等多元声音，不抹掉任何一方代表性观点。",
                "反对网络暴力与人身攻击，对情绪化的攻击性表达一律不予收录。",
                "风向占比仅供信息参考，不构成投资、消费或任何决策建议。",
              ].map((t, i) => (
                <li key={i} className="flex gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed text-foreground-700">{t}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 里程碑金句 */}
        <section className="wrap py-14 md:py-20">
          <div className="rounded-3xl bg-background-100/70 p-6 md:p-10">
            <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-quill-pen-line" aria-hidden="true" /> 我们相信
            </span>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {milestones.map((m, i) => (
                <div key={i} className="rounded-2xl border border-background-200 bg-background-50 p-6">
                  <span className="font-display text-3xl font-bold text-accent-500">0{i + 1}</span>
                  <p className="mt-3 font-heading text-lg font-semibold leading-relaxed text-foreground-900">{m}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 口碑 */}
        <section className="wrap">
          <div className="mb-8 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
                <i className="ri-heart-3-line" aria-hidden="true" /> 用户口碑
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">读者都在说</h2>
            </div>
            <p className="text-sm text-foreground-500">来自读者的真实反馈（示例摘录）</p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {quotes.slice(0, 3).map((q, i) => (
              <figure key={i} className="card-tint rounded-2xl p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true" />
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{q.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-bold text-secondary-800">
                    {q.author.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-800">{q.author}</span>
                    <span className="text-xs text-foreground-500">{q.role}</span>
                  </span>
                  <i className="ri-star-fill ml-auto text-accent-500" aria-hidden="true" />
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="wrap mt-16" id="faq">
          <div className="mb-8">
            <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-question-answer-line" aria-hidden="true" /> 常见问题
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold text-foreground-900">你可能会问</h2>
          </div>
          <FaqAccordion />
        </section>

        {/* 联系 */}
        <section className="wrap mt-16 pb-4">
          <div className="rounded-3xl border border-background-200 bg-background-100/70 p-8 md:p-12">
            <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground-900 md:text-3xl">想投稿 / 想合作 / 有建议？</h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                  如果你发现了值得被汇总的热议话题，或对内容呈现有建议，欢迎通过邮件与我们联系。我们会认真阅读每一条来信。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={`mailto:${siteInfo.mail}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    rel="nofollow"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true" /> {siteInfo.mail}
                  </a>
                  <Link
                    to="/rank"
                    className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-200 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    先去围观热议榜 <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              </div>
              <div className="rounded-2xl bg-foreground-900 p-6 text-background-50">
                <p className="font-label text-lg text-accent-200">我们的邮箱</p>
                <p className="mt-3 break-all font-display text-xl text-background-50">{siteInfo.mail}</p>
                <p className="mt-4 text-xs leading-relaxed text-background-300">
                  示例联系方式，仅用于页面展示；真实合作请通过上线后的正式渠道。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollTop />
    </div>
  );
}